import { ref } from 'firebase/storage';
import { S3Client, PutObjectCommand, DeleteObjectCommand } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";

// Environment variable to determine storage type
const STORAGE_TYPE = process.env.REACT_APP_STORAGE_TYPE || 'minio'; // values: 'minio', 'firebase'

// Environment variables for minio storage
const ENDPOINT = process.env.REACT_APP_MINIO_ENDPOINT || 'https://fbqmuseumstg.museumsense.io';
const ACCESS_KEY = process.env.REACT_APP_MINIO_ACCESS_KEY || 'ROOTNAME';
const SECRET_KEY = process.env.REACT_APP_MINIO_SECRET_KEY || 'CHANGEME123';
const BUCKET = process.env.REACT_APP_MINIO_BUCKET || 'local-dev';

// Abstract Storage Interface
class StorageInterface {
	async uploadImages(originalFile, thumbnail) {
		throw new Error('uploadImages must be implemented');
	}

	async uploadDocument(file) {
		throw new Error('uploadDocuments must be implemented');
	}

	async uploadMedia(file, onProgress) {
		throw new Error('uploadMedia must be implemented');
	}

	async deleteFiles(fileName) {
		throw new Error('deleteFiles must be implemented');
	}
}

// Firebase Storage Implementation
class FirebaseStorage extends StorageInterface {
	constructor(storageFolder) {
		super();
		this.client = ref(window.location.hostname.split('.')[0] + '/' + storageFolder);
	}

	async uploadImages(originalFile, thumbnail) {
		const originalRef = this.client.child(originalFile.name);

		const originalSnapshot = await originalRef.put(originalFile, { cacheControl: 'public, max-age=86400' });
		const originalUrl = await originalSnapshot.ref.getDownloadURL();

		let thumbUrl = originalUrl;
		if (thumbnail) {
			const thumbRef = this.client.child(`thumb_${originalFile.name}`);
			const thumbSnapshot = await thumbRef.put(thumbnail, { cacheControl: 'public, max-age=86400' });
			thumbUrl = await thumbSnapshot.ref.getDownloadURL();
		}

		return { originalUrl, thumbUrl };
	}

	async uploadDocument(originalFile) {
		const originalRef = this.client.child(originalFile.name);
		const originalSnapshot = await originalRef.put(originalFile, { cacheControl: 'public, max-age=86400' });
		const originalUrl = await originalSnapshot.ref.getDownloadURL();
		return { originalUrl };
	}

	async uploadMedia(file, onProgress) {
		const originalRef = this.client.child(file.name);
		const task = originalRef.put(file, { cacheControl: 'public, max-age=86400' });
		if (onProgress) {
			task.on('state_changed', snapshot => {
				onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
			});
		}
		const originalSnapshot = await task;
		const originalUrl = await originalSnapshot.ref.getDownloadURL();
		return { originalUrl };
	}

	async deleteFiles(fileName) {
		await Promise.all([
			this.client.child(fileName).delete(),
			this.client.child(`thumb_${fileName}`).delete(),
		]);
	}
}

// MinIO Storage Implementation
class MinioStorage extends StorageInterface {
	constructor(storageFolder) {
		super();
		this.client = new S3Client({
			region: 'us-east-1',
			endpoint: ENDPOINT,
			credentials: {
				accessKeyId: ACCESS_KEY,
				secretAccessKey: SECRET_KEY,
			},
			forcePathStyle: true,
			requestChecksumCalculation: "WHEN_REQUIRED",
		});
		this.bucket = BUCKET;
		this.baseUrl = ENDPOINT;
		this.storageFolder = window.location.hostname.split('.')[0] + '/' + storageFolder;
	}

	async uploadImages(originalFile, thumbnail) {
		const originalParams = {
			Bucket: this.bucket,
			Key: `${this.storageFolder}/${originalFile.name}`,
			Body: originalFile,
			ContentType: originalFile.type,
			CacheControl: 'public, max-age=86400',
		};

		await this.client.send(new PutObjectCommand(originalParams));

		if (thumbnail) {
			const thumbParams = {
				Bucket: this.bucket,
				Key: `${this.storageFolder}/thumb_${originalFile.name}`,
				Body: thumbnail,
				ContentType: thumbnail.type,
				CacheControl: 'public, max-age=86400',
			};

			await this.client.send(new PutObjectCommand(thumbParams))
		}

		const originalUrl = `${this.baseUrl}/${this.bucket}/${this.storageFolder}/${originalFile.name}`;
		return {
			originalUrl: originalUrl,
			thumbUrl: thumbnail ? `${this.baseUrl}/${this.bucket}/${this.storageFolder}/thumb_${originalFile.name}` : originalUrl,
		};
	}

	async uploadDocument(file) {
		const params = {
			Bucket: this.bucket,
			Key: `${this.storageFolder}/${file.name}`,
			Body: file,
			ContentType: file.type,
			CacheControl: 'public, max-age=86400',
		};

		await this.client.send(new PutObjectCommand(params));
		return {
			originalUrl: `${this.baseUrl}/${this.bucket}/${this.storageFolder}/${file.name}`,
		};
	}

	// Multipart upload: gives real byte progress and retries failed parts automatically,
	// which matters for large video/audio files (a plain PutObjectCommand does neither).
	async uploadMedia(file, onProgress) {
		const upload = new Upload({
			client: this.client,
			params: {
				Bucket: this.bucket,
				Key: `${this.storageFolder}/${file.name}`,
				Body: file,
				ContentType: file.type,
				CacheControl: 'public, max-age=86400',
			},
		});

		if (onProgress) {
			upload.on('httpUploadProgress', (progress) => {
				if (progress.loaded && progress.total) {
					onProgress(Math.round((progress.loaded / progress.total) * 100));
				}
			});
		}

		await upload.done();
		return {
			originalUrl: `${this.baseUrl}/${this.bucket}/${this.storageFolder}/${file.name}`,
		};
	}

	async deleteFiles(fileName) {
		const deleteParams = [
			new DeleteObjectCommand({
				Bucket: this.bucket,
				Key: `${this.storageFolder}/${fileName}`,
			}),
			new DeleteObjectCommand({
				Bucket: this.bucket,
				Key: `${this.storageFolder}/thumb_${fileName}`,
			}),
		];
		await Promise.all(deleteParams.map(params => this.client.send(params)));
	}
}

// Factory function to get storage service
export function getStorageService(storageFolder) {
	switch (STORAGE_TYPE) {
		case 'firebase':
			return new FirebaseStorage(storageFolder);
		case 'minio':
			return new MinioStorage(storageFolder);
		default:
			throw new Error(`Unsupported storage type: ${STORAGE_TYPE}`);
	}
}